import { Suspense, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, RoundedBox, Sparkles, useTexture, ContactShadows } from "@react-three/drei";
import { StudioEnv } from "./GoldDust";

const GOLD = { color: "#e2b94a", metalness: 0.92, roughness: 0.16, clearcoat: 0.5, clearcoatRoughness: 0.15, envMapIntensity: 2.6, emissive: "#3a2a08", emissiveIntensity: 0.35 };
const FACE = { color: "#e6c460", metalness: 0.7, roughness: 0.3, envMapIntensity: 2.2, emissive: "#6b4f12", emissiveIntensity: 0.7 };

function Medallion() {
  const tex = useTexture("/brand/logo-seal.png");
  const ref = useRef();
  useFrame((s, d) => {
    if (!ref.current) return;
    ref.current.rotation.y += d * 0.55;
    ref.current.rotation.x = 0.35 + s.pointer.y * 0.25;
  });
  return (
    <Float speed={1.2} rotationIntensity={0.15} floatIntensity={0.8}>
      <group ref={ref} position={[0.6, 0.25, 0]}>
        <mesh rotation-x={Math.PI / 2} castShadow>
          <cylinderGeometry args={[1.15, 1.15, 0.14, 128]} />
          <meshPhysicalMaterial attach="material-0" {...GOLD} roughness={0.22} />
          <meshPhysicalMaterial attach="material-1" {...FACE} map={tex} />
          <meshPhysicalMaterial attach="material-2" {...FACE} map={tex} />
        </mesh>
        <mesh rotation-x={Math.PI / 2}>
          <torusGeometry args={[1.2, 0.045, 24, 160]} />
          <meshPhysicalMaterial {...GOLD} roughness={0.1} />
        </mesh>
      </group>
    </Float>
  );
}

function Bottle() {
  return (
    <Float speed={1.6} rotationIntensity={0.35} floatIntensity={1.4}>
      <group position={[-1.9, -0.7, -0.4]} rotation={[0.1, 0.5, 0.05]}>
        <RoundedBox args={[0.9, 1.35, 0.5]} radius={0.08} smoothness={6}>
          <meshPhysicalMaterial color="#3b2a0c" metalness={0.2} roughness={0.06} transmission={0.55} thickness={1.2} ior={1.5} clearcoat={1} envMapIntensity={2.4} emissive="#1a1204" emissiveIntensity={0.4} />
        </RoundedBox>
        <mesh position={[0, 0.05, 0.26]}>
          <planeGeometry args={[0.5, 0.36]} />
          <meshPhysicalMaterial {...GOLD} roughness={0.3} />
        </mesh>
        <mesh position={[0, 0.92, 0]}>
          <cylinderGeometry args={[0.24, 0.26, 0.5, 48]} />
          <meshPhysicalMaterial {...GOLD} roughness={0.12} />
        </mesh>
      </group>
    </Float>
  );
}

function Watch() {
  const hands = useRef();
  useFrame((s) => {
    if (hands.current) hands.current.rotation.z = -s.clock.elapsedTime * 0.9;
  });
  return (
    <Float speed={1.4} rotationIntensity={0.5} floatIntensity={1.1}>
      <group position={[2.35, -1.05, -0.2]} rotation={[0.3, -0.6, 0]}>
        <mesh>
          <torusGeometry args={[0.62, 0.11, 32, 96]} />
          <meshPhysicalMaterial {...GOLD} roughness={0.1} />
        </mesh>
        <mesh position={[0, 0, -0.02]}>
          <circleGeometry args={[0.58, 64]} />
          <meshPhysicalMaterial color="#0b1a2e" metalness={0.6} roughness={0.25} envMapIntensity={1.4} />
        </mesh>
        <group ref={hands} position={[0, 0, 0.02]}>
          <mesh position={[0, 0.2, 0]}><boxGeometry args={[0.035, 0.44, 0.01]} /><meshPhysicalMaterial {...GOLD} /></mesh>
          <mesh position={[0.12, 0, 0]} rotation-z={-Math.PI / 2}><boxGeometry args={[0.03, 0.3, 0.01]} /><meshPhysicalMaterial {...GOLD} /></mesh>
        </group>
        <mesh position={[0.7, 0, 0]} rotation-z={Math.PI / 2}>
          <cylinderGeometry args={[0.05, 0.05, 0.12, 24]} />
          <meshPhysicalMaterial {...GOLD} />
        </mesh>
      </group>
    </Float>
  );
}

function Rig({ children, position = [0, 0, 0], scale = 1 }) {
  const ref = useRef();
  useFrame((s) => {
    if (!ref.current) return;
    ref.current.rotation.y += (s.pointer.x * 0.35 - ref.current.rotation.y) * 0.05;
    ref.current.rotation.x += (-s.pointer.y * 0.18 - ref.current.rotation.x) * 0.05;
  });
  return <group ref={ref} position={position} scale={scale}>{children}</group>;
}

export default function HeroScene({ active = true, position, scale }) {
  return (
    <Canvas dpr={1} camera={{ position: [0, 0, 7.2], fov: 38 }} frameloop={active ? "always" : "never"} gl={{ alpha: true, antialias: true, powerPreference: "high-performance" }}>
      <ambientLight intensity={0.25} />
      <spotLight position={[5, 6, 6]} intensity={60} angle={0.5} penumbra={0.8} color="#ffe6b0" />
      <pointLight position={[-4, -2, 3]} intensity={18} color="#d4af37" />
      <pointLight position={[0, 1, 4]} intensity={10} color="#fff1c4" />
      <Suspense fallback={null}>
        <StudioEnv />
        <Rig position={position} scale={scale}>
          <Medallion />
          <Bottle />
          <Watch />
        </Rig>
        <Sparkles count={60} scale={[10, 7, 4]} size={2.4} speed={0.25} color="#E9C767" opacity={0.7} />
        <ContactShadows position={[0, -2.6, 0]} opacity={0.5} scale={12} blur={2.6} far={4} color="#000" />
      </Suspense>
    </Canvas>
  );
}
